import { useSuccessionInsights } from "@/hooks/useSuccessionInsights";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export function InsightsTileContent() {
  const { insights, counts, isLoading } = useSuccessionInsights();
  const { user } = useAuth();

  const { data: completedCount = 0 } = useQuery({
    queryKey: ["insightsTileCompletedCount", user?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from("assessment_modules")
        .select("module_id")
        .eq("user_id", user!.id)
        .eq("status", "complete");
      return data?.length ?? 0;
    },
    enabled: !!user?.id,
  });

  if (isLoading) {
    return <span className="italic text-muted-foreground">Data loading…</span>;
  }

  // Show prompt if no assessments completed yet
  if (completedCount === 0) {
    return (
      <div className="flex flex-col items-center gap-1">
        <p className="text-sm font-medium text-muted-foreground">
          Complete your first assessment to unlock insights
        </p>
      </div>
    );
  }

  if (counts.total === 0) {
    return (
      <div className="flex flex-col items-center gap-1">
        <p className="text-sm font-medium text-[#16a34a]">
          No risks identified — your plan looks strong
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center gap-2">
      <span className="text-3xl font-bold text-foreground">{counts.total}</span>
      <span className="text-xs text-muted-foreground">Click to view Succession Insights</span>
      <div className="flex flex-wrap gap-1.5 justify-center mt-1">
        {counts.high > 0 && (
          <span className="rounded-full bg-[#ef4444]/10 px-2.5 py-0.5 text-[11px] font-medium text-[#ef4444]">
            🔴 {counts.high} High priority
          </span>
        )}
        {counts.medium > 0 && (
          <span className="rounded-full bg-amber-500/10 px-2.5 py-0.5 text-[11px] font-medium text-amber-600">
            🟡 {counts.medium} Medium priority
          </span>
        )}
        {counts.on_track > 0 && (
          <span className="rounded-full bg-[#16a34a]/10 px-2.5 py-0.5 text-[11px] font-medium text-[#16a34a]">
            🟢 {counts.on_track} On track
          </span>
        )}
      </div>
    </div>
  );
}
